/**
 * Email Service - EmailJS Integration
 * 
 * Client-side email service using EmailJS
 * Simple and reliable email delivery
 */

import emailjs from '@emailjs/browser';
import emailjsConfig from '../config/emailjsConfig';

// Initialize EmailJS
const initializeEmailJS = () => {
  if (emailjsConfig.publicKey && emailjsConfig.publicKey !== 'YOUR_PUBLIC_KEY') {
    emailjs.init(emailjsConfig.publicKey);
  }
};

// Main email function with auto-detection
export const sendEmail = async (emailData, method = 'auto') => {
  console.log('📧 Using EmailJS for email delivery...');
  
  // Initialize EmailJS
  initializeEmailJS();
  
  if (method === 'auto') {
    // Auto-detect email type based on data
    if (emailData.type === 'review_confirmation' || emailData.type === 'review_completion') {
      return await sendReviewEmail(emailData);
    } else {
      return await sendContactEmail(emailData);
    }
  } else if (method === 'contact') {
    return await sendContactEmail(emailData);
  } else if (method === 'review') {
    return await sendReviewEmail(emailData);
  } else {
    throw new Error(`Unknown email method: ${method}`);
  }
};

// Send contact/inquiry email
export const sendContactEmail = async (emailData) => {
  try {
    console.log('📧 EmailJS template parameters:', {
      from_name: emailData.name,
      from_email: emailData.email,
      phone: emailData.phone,
      service: emailData.service,
      message: emailData.message,
      source: emailData.source,
      source_tag: emailData.sourceTag
    });
    
    // Try multiple parameter name formats for compatibility
    const templateParams = {
      // Standard EmailJS format
      from_name: emailData.name || 'Unknown',
      from_email: emailData.email || 'Not provided',
      phone: emailData.phone || 'Not provided',
      service: emailData.service || 'General Inquiry',
      message: emailData.message || 'No message provided',
      sms_consent: emailData.smsConsent || 'No',
      source: emailData.source || 'website',
      source_tag: emailData.sourceTag || 'Website',
      to_email: 'hi@rcroof.co',
      
      // Alternative format (in case template uses different names)
      name: emailData.name || 'Unknown',
      email: emailData.email || 'Not provided',
      user_name: emailData.name || 'Unknown',
      user_email: emailData.email || 'Not provided',
      client_name: emailData.name || 'Unknown',
      client_email: emailData.email || 'Not provided',
      sms_consent: emailData.smsConsent || 'No',
      tracking_source: emailData.source || 'website',
      submission_source: emailData.source || 'website',
      lead_tag: emailData.sourceTag || 'Website'
    };

    const result = await emailjs.send(
      emailjsConfig.serviceId,
      emailjsConfig.templates.contact,
      templateParams
    );

    return {
      success: true,
      message: 'Contact email sent successfully via EmailJS',
      status: result.status,
      text: result.text
    };
  } catch (error) {
    console.error('EmailJS contact email error:', error);
    throw new Error(`EmailJS contact email failed: ${error.message}`);
  }
};

// Send review confirmation email
export const sendReviewEmail = async (emailData) => {
  try {
    const templateParams = {
      client_name: emailData.client_name || 'Unknown Client',
      client_phone: emailData.client_phone || 'Not provided',
      review_platforms: emailData.review_platforms || 'Unknown Platform',
      review_count: emailData.review_count || '0',
      timestamp: new Date().toLocaleString(),
      to_email: 'info@rcroofing.com' // Your business email
    };

    const result = await emailjs.send(
      emailjsConfig.serviceId,
      emailjsConfig.templates.review,
      templateParams
    );

    return {
      success: true,
      message: 'Review email sent successfully via EmailJS',
      status: result.status,
      text: result.text
    };
  } catch (error) {
    console.error('EmailJS review email error:', error);
    throw new Error(`EmailJS review email failed: ${error.message}`);
  }
};

// Email templates for different types of emails
export const emailTemplates = {
  reviewCompletion: (data) => ({
    type: 'review_completion',
    client_name: data.userInfo?.name || 'Unknown Client',
    client_phone: data.userInfo?.phone || 'Not provided',
    review_platforms: data.platformName || 'Unknown Platform',
    review_count: 1,
    timestamp: data.timestamp,
    user_agent: data.userAgent,
    message: `Review completed on ${data.platformName} at ${data.timestamp}`
  })
};

// Export main functions
export default {
  sendEmail,
  sendContactEmail,
  sendReviewEmail,
  emailTemplates
};